import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { capitalizer, formatAmount, formatPercentage, truncate } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onComparatorBiddingsThunk } from './ComparatorBiddings.actions'
import ColumnChart from '../../components/Charts/ColumnChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import InfoLabel from '../../components/Labels/InfoLabel'
import ComparadorBiddingsDistributions from '../../components/Infographics/ComparadorBiddingsDistributions'
import { Wrapper, GridWrapper, Link } from './ComparatorBiddings.styles'

/**
 * The ComparatorBiddings' container.
 */
const ComparatorBiddings = ({ firstEntity, secondEntity, latestUpdate }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const dataTableRef = useRef()
  const inputColumnChartRef = useRef()
  const { endYear, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Licitaciones_${years[0].value}`)
  const {
    data: { chart, categories, csvStructure },
    fetching: loading
  } = useSelector(state => state.comparatorBiddings)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(
        onComparatorBiddingsThunk(
          { year: updatedValue, idEntity: [firstEntity.value, secondEntity.value] },
          distValue,
          firstEntity.name,
          secondEntity.name
        )
      )
      setFileName(`Licitaciones_${updatedValue}`)
    }
  })
  const { value: distValue, onChange: handleChangeDist } = useSelect({
    initialValue: 'amount',
    changeCallback: updatedValue => {
      dispatch(
        onComparatorBiddingsThunk(
          { year: yearValue, idEntity: [firstEntity.value, secondEntity.value] },
          updatedValue,
          firstEntity.name,
          secondEntity.name
        )
      )
    }
  })
  const handleShowTable = updatedValue => () => setShowTable(updatedValue)
  const distribution = [
    { name: 'Monto transado en pesos', value: 'amount' },
    { name: 'N° de Órdenes compra', value: 'oc' },
    { name: 'Licitaciones adjudicadas', value: 'lic' },
    { name: 'Monto porcentual', value: 'percentage' }
  ]

  const handleCaptureImage = () => exportToImage('ComparatorBiddings', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    dispatch(
      onComparatorBiddingsThunk(
        { year: years[0].value, idEntity: [firstEntity.value, secondEntity.value] },
        'amount',
        firstEntity.name,
        secondEntity.name
      )
    )
  }, [firstEntity, secondEntity])

  return (
    <Wrapper margin="64px auto 0">
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Distribución de sus licitaciones
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Las licitaciones son un procedimiento administrativo efectuado en forma autónoma por cada organismo
            comprador. Se publica una invitación abierta a través de Mercado Público a los proveedores que puedan
            proporcionar un bien o servicio. Posteriormente se selecciona y adjudica la oferta más conveniente, según
            los criterios que se establezcan en las bases de licitación
            <br />
            <br />A continuación podrás conocer cómo se distribuyen los montos transados, el número de órdenes de compra
            y el número de licitaciones distribuidas según sus montos (L1, LE, LP, LQ, entre otros). Recuerda que estos
            montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            La información referente a las licitaciones se basa en todas aquellas que se encuentren en estado
            “Adjudicada” o “Re-adjudicada”, y la información de montos transados dentro de esta sección corresponde al
            monto bruto (monto neto + impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={10} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearValue} onChange={handleChangeYear} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleChangeDist} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12}>
          <IconButton
            singleIcon
            disabled={!showTable}
            margin="24px 10px 0 0"
            type="toggleChart"
            variant="outlined"
            onClick={handleShowTable(false)}
          />
          <IconButton
            singleIcon
            disabled={showTable}
            margin="24px 0 0"
            type="toggleTable"
            variant="outlined"
            onClick={handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      {showTable && (
        <GridWrapper container justifyContent="center" margin="24px 0 0">
          <GridWrapper item xs={12} md={10}>
            <DataTable
              headers={[
                { label: 'Organismo comprador', value: 0 },
                { label: 'Año', value: 1 },
                { label: 'Categoría Licitación', value: 2 },
                { label: 'N° órdenes de compra', value: 3 },
                { label: 'N° licitaciones', value: 4 },
                { label: 'Monto total transado', value: 5 },
                { label: 'Monto porcentual', value: 6 }
              ]}
              headersCellsProps={[
                { enableSort: true },
                null,
                { enableSort: true },
                { enableSort: true },
                { enableSort: true },
                { enableSort: true },
                { enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={csvStructure.slice(1)}
              recordsDecorators={[
                ([entity]) => truncate(capitalizer(entity), 40),
                ([, year]) => year,
                ([, , category]) => category,
                ([, , , totalOCs]) => totalOCs,
                ([, , , , totalBiddings]) => totalBiddings,
                ([, , , , , amount]) => `$${formatAmount(amount)}`,
                ([, , , , , , percentage]) => formatPercentage(percentage)
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        </GridWrapper>
      )}
      {!showTable && (
        <GridWrapper container justifyContent="center" margin="32px 0 0">
          <GridWrapper item xs={12}>
            {distValue === 'amount' && <InfoLabel label="Millones de pesos (CLP)" margin="0 0 16px" />}
            {distValue === 'oc' && <InfoLabel label="N° de órdenes de compra" margin="0 0 16px" />}
            {distValue === 'lic' && <InfoLabel label="N° de licitaciones" margin="0 0 16px" />}
            {distValue === 'percentage' && <InfoLabel label="Monto porcentual" margin="0 0 16px" />}
            <ColumnChart
              categories={categories}
              data={chart}
              distValue={distValue}
              fileName={fileName}
              id="ComparatorBiddings"
              inputColumnChartRef={inputColumnChartRef}
            />
          </GridWrapper>
        </GridWrapper>
      )}
      {!showTable && (
        <GridWrapper container justifyContent="center">
          <GridWrapper item xs={12}>
            <InfoLabel color={theme.palette.charts.navyBlue} label={firstEntity.name} margin="0 24px 0 0" />
            <InfoLabel color={theme.palette.charts.darkRed} label={secondEntity.name} />
          </GridWrapper>
        </GridWrapper>
      )}
      <GridWrapper container justifyContent="center" margin="48px 0 0">
        <GridWrapper item xs={12}>
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<ComparadorBiddingsDistributions />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

ComparatorBiddings.propTypes = {
  firstEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  secondEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  latestUpdate: PropTypes.string
}

export default ComparatorBiddings
